import { useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { BrandLogo } from "../components/BrandLogo";
import { TwoFactorStep } from "../components/TwoFactorStep";

import { tr } from "../lib/i18n";
export default function LoginPage() {
  const { user, login, completeLogin } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const back = useCallback(() => { setMfa(null); setPassword(""); }, []);
  const done = (data) => {
    const u = completeLogin(data);
    nav(u.must_change_password ? "/change-password" : "/dashboard");
  };
  const name = settings.showroom_name || tr("x.showroom");
  const logo = settings.logo_dark_url || settings.logo_light_url;
  const lightLogo = settings.logo_light_url || settings.logo_dark_url;

  if (user && user !== false) {
    return <Navigate to={user.must_change_password ? "/change-password" : "/dashboard"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(identifier, password);
      if (u.mfa) setMfa(u);
      else nav(u.must_change_password ? "/change-password" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[hsl(var(--background))]">
      {/* Left: brand panel */}
      <div className="hidden md:flex relative overflow-hidden bg-[#141416] text-white flex-col justify-between p-12 lg:p-16" data-testid="login-brand-panel">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 80% at 0% 0%, color-mix(in srgb, var(--accent) 35%, #141416) 0%, #141416 60%)" }} />
        <div className="relative z-10 flex items-center min-h-[48px]" data-testid="login-brand">
          <BrandLogo src={logo} name={name} testid="login-brand" imgStyle={{ height: 48, width: "auto", maxWidth: 220, objectFit: "contain" }} nameClassName="font-semibold text-xl" nameStyle={{ fontFamily: "Outfit" }} />
        </div>
        <div className="relative z-10">
          <div className="eyebrow mb-3" style={{ color: "rgba(255,255,255,.72)" }}>{tr("x.internal_portal")}</div>
          <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-tight" style={{ fontFamily: "Outfit" }}>
            {tr("x.precision_inventory")}<br />
            <span style={{ color: "var(--accent-300)" }} data-testid="login-heading-accent">{tr("x.every_showroom_day")}</span>
          </h1>
          <p className="mt-5 text-white/75 max-w-md">
            {tr("x.stock_sales_consignment_costing_and_audi")}
          </p>
        </div>
        <div className="text-xs text-white/60 relative z-10">© {new Date().getFullYear()} Showroom 360</div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        {mfa ? (
          <div className="w-full max-w-sm space-y-6">
            <div className="md:hidden flex items-center min-h-[40px]">
              <BrandLogo src={lightLogo} name={name} testid="mfa-mobile-brand" imgStyle={{ height: 40, width: "auto", maxWidth: 200, objectFit: "contain" }} nameClassName="font-semibold text-lg" nameStyle={{ fontFamily: "Outfit" }} />
            </div>
            <TwoFactorStep stage={mfa.mfa} mfaToken={mfa.mfa_token} onDone={done} onBack={back} />
          </div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="md:hidden flex items-center min-h-[40px]" data-testid="login-mobile-brand">
            <BrandLogo src={lightLogo} name={name} testid="login-mobile-brand" imgStyle={{ height: 40, width: "auto", maxWidth: 200, objectFit: "contain" }} nameClassName="font-semibold text-lg" nameStyle={{ fontFamily: "Outfit" }} />
          </div>
          <div>
            <div className="eyebrow mb-2">{tr("x.sign_in")}</div>
            <h2 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>
              {tr("x.welcome_back")}
            </h2>
            <p className="text-sm text-[var(--gray-400)] mt-1">{tr("x.enter_your_credentials_to_access_the_das")}</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-[var(--gray-400)]">{tr("x.email_or_username")}</label>
              <input
                data-testid="login-identifier"
                autoFocus
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="mt-2 w-full h-11 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 text-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-ring)]"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-[var(--gray-400)]">{tr("x.password")}</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full h-11 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 text-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-ring)]"
                required
              />
            </div>
          </div>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full h-11 rounded-lg bg-accent-brand font-semibold text-sm disabled:opacity-60"
          >
            {busy ? tr("x.signing_in") : tr("x.sign_in")}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
